import React from 'react'
import clsx from 'clsx'
import lang from '@/lang'
import { makeStyles } from '@mui/styles'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'

import { TextInput } from '@/modules/base/components/inputs'
import { useAppSelector } from '@/redux/store'
import { useUpgradeModal } from '@/hooks/useUpgradeModal'
import { isMeinBuero } from '@/utils/build-env'
import { useNavigate } from 'react-router'

const isMB = isMeinBuero()

const useStyles = makeStyles((theme) => ({
  ownEmailHint: {
    fontSize: theme.fontSizes.smaller,
    color: theme.palette.amber[800],
    marginTop: 8,
    display: 'flex',
    gap: 8,
    '& a': {
      color: theme.primaryV2,
      fontSize: theme.fontSizes.small,
      cursor: 'pointer',
      marginLeft: 4,
    },
  },
  boxed: {
    background: theme.palette.warning.background,
    borderRadius: theme.borderRadius,
    padding: '12px 16px',
    marginTop: 0,
    alignItems: 'flex-start',
    '& svg': {
      marginTop: 2,
    },
    '& a': {
      display: 'block',
      marginTop: 4,
      marginLeft: 0,
    },
  },
}))

interface IOwnEmailRestriction {
  className?: string
  label?: string
  hint?: string
  hideInput?: boolean
  hideIcon?: boolean
  // Renders as a standalone amber notice box instead of the plain inline hint - use when a
  // single combined message covers several fields (e.g. recipients + CC + BCC) rather than
  // repeating the inline hint under each one.
  boxed?: boolean
}

const OwnEmailRestriction = (props: IOwnEmailRestriction) => {
  const {
    className,
    label = lang.ownEmailRestriction.label.default,
    hint = lang.ownEmailRestriction.hint.default,
    hideInput = false,
    hideIcon = false,
    boxed = false,
  } = props
  const classes = useStyles()
  const allowExternalEmails = useAppSelector(state => state.tenant.settings?.allowExternalEmails)
  const email = useAppSelector(state => state.user.info.email)
  const openUpgradeModal = useUpgradeModal()
  const navigate = useNavigate()

  if (allowExternalEmails) { return null }

  const onLinkClick = () => {
    if (isMB) {
      return navigate('/shop/invoices')
    }

    openUpgradeModal()
  }

  return (
    <div className={className}>
      {!hideInput && (
        <div>
          <TextInput
            name="own-email-restriction-input"
            value={email}
            label={label}
            disabled
            dataQsId="own-email-restriction-input"
          />
        </div>
      )}
      <div className={clsx(classes.ownEmailHint, boxed && classes.boxed)}>
        {!hideIcon && <WarningAmberRoundedIcon fontSize='medium' color='warning' />}
        <div>
          {hint}
          <a onClick={onLinkClick}>
            {lang.generics.upgradeNow}
          </a>
        </div>
      </div>
    </div>
  )
}

export default OwnEmailRestriction
